import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Form, Select, TextField } from "../../../components/forms";
import Stepper from "./Stepper";
import {
  useGetMyProfileQuery,
  useUpdateProfileMutation,
} from "../../../services/userService";
import { toast } from "react-toastify";
import parseError from "../../../utils/errorUtils";
import { useAddCheckoutPaymentConfirmMutation } from "../../../services/paymentService";
import { useLoggerUtils } from "../../../utils/loggerUtils";
import {
  useUpdateHubSpotMutation
} from "../../../services/dataService"
import { useGetAppointmentsQuery, useAddOrderMutation } from "../../../services/orderService";
import dayjs from "dayjs";

const OrderSuccess = () => {
  const navigate = useNavigate();
  const { data: profileData } = useGetMyProfileQuery();
  const [updateProfile] = useUpdateProfileMutation();
  const { info } = useLoggerUtils();
  const [isMobile, setIsMobile] = useState(false);

  const handleNavigate = () =>
    navigate(`/onboarding/eligibility-flow/gp-details`, { replace: true });

  const validationSchema = Yup.object().shape({
    referredBy: Yup.string().required("Please select an option"),
    other: Yup.string().when("referredBy", {
      is: "other",
      then: Yup.string().required("Please specify the details for 'Other'"),
      otherwise: Yup.string().notRequired(),
    }),
  });

  const [addCheckoutPaymentConfirm] = useAddCheckoutPaymentConfirmMutation();
  const sessionId = localStorage.getItem("sessionId");
  // const orderItemId = localStorage.getItem("orderId");
  const paymentMode = localStorage.getItem("paymentMode")
  const consultationDate = localStorage.getItem("appointment_full_date");
  const [refreshKey, setRefreshKey] = useState(1);
  const [referredByState, setReferredByState] = useState(null);
  const [disable, setDisable] = useState(true);
  const [updateHubSpot] = useUpdateHubSpotMutation();
  const { data: appointmentList, isFetching } = useGetAppointmentsQuery({
    page: 0,
    pageSize: 320,
    type: "full",
  });
  const [addOrder] = useAddOrderMutation();

  const handleChange = (event) => {
    setReferredByState(event.target.value);
  };

  const initialValues = {};

  const handleSubmit = async (values) => {
    let data = {
      ...profileData,
      preferences: {
        ...profileData.preferences,
        referredBy: values.referredBy === "other" ? `other: ${values.other}` : values.referredBy,
      },
    };

    try {
      await validationSchema.validate(values, { abortEarly: false });

      const res = await updateProfile({ values: data });

      if (res.error) {
        toast.error(parseError(res.error, "Couldn't complete request"));
      } else {
        !isMobile && toast.success("Successfully saved");
        handleNavigate();
      }
    } catch (e) {
      if (e.name === "ValidationError") {
        e.inner.forEach(error => {
          toast.error(error.message);
        });
      } else {
        console.error(e);
      }
    }
  };

  useEffect(async () => {
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    setIsMobile(isMobile);
    setTimeout(() => {
      setRefreshKey((prev) => prev + 1);
    }, 2000);

    try {
      await submitHubSpot();
      !localStorage.getItem("orderId") && await handleSubmitOrder();
    } catch (e) {
      info("addOrder - catch: " + JSON.stringify(e));
      console.error(e);
    }
  }, [])

  const handleSubmitOrder = async () => {
    const orderData = {
      deliveryType: "STANDARD",
      paymentType: "ONLINE",
      deviceType: "OTHER",
      orderNotes: [],
      name: `Consultation (${consultationDate})`,
      type: "BOOKING",
      appointment: {
        id: Number(localStorage.getItem("appointment_id")),
      },
      patient: profileData,
      isRepeatOrder: false,
    };

    try {
      let payload = orderData;
      if (profileData?.preferences?.TEMPORARY_APPOINTMENT) {
        payload = { ...orderData, source: 'CALL_BOOKING' };
      }
      const res = await addOrder({ type: "BOOKING", values: payload });

      if (res.error) {
        info("addOrder: " + JSON.stringify(res));
        if (localStorage.getItem("appointment_id") === "null") {
          toast.error("Select an appointment");
        } else {
          toast.error(parseError(res.error, "Couldn't place order"));
        }
      } else {
        localStorage.setItem("orderId", res.data.message);
        await paymentConfirm(res.data.message)
      }
    } catch (e) {
      info("addOrder - catch: " + JSON.stringify(e));
      console.error(e);
    }
  };

  const paymentConfirm = async (orderItemId) => {
    try {
      const res = await addCheckoutPaymentConfirm(
        {
          sessionId: sessionId,
          orderItemId: orderItemId,
          paymentMode: paymentMode
        }
      );

      if (res.error) {
        toast.error(parseError(res.error, "Save transaction is failed"));
      }
    } catch (e) {
      info("addOrder - catch: " + JSON.stringify(e));
      console.error(e);
    }
  }

  const addressParts = [
    profileData?.address?.line1,
    profileData?.address?.line2,
    profileData?.address?.city,
    profileData?.address?.country,
  ];

  const fullAddress = addressParts.filter(Boolean).join(", ");

  useEffect(() => {
    setDisable(!referredByState)
  }, [referredByState])

  const submitHubSpot = async () => {
    const vid = localStorage.getItem("vid")
    try {
      let data = {
        properties: [
          {
            property: "payment",
            value: "Completed"
          }
        ]
      };
      const updateRes = vid && await updateHubSpot({ values: data, vid: vid })

      if (updateRes.data.status === "Success") {
      } else {
        console.error("hubspot faild")
      }
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <div className="d-flex flex-column h-100">
      <h4 className="mb-2 fw-bold">
        Thank you for booking your appointment with us!
      </h4>
      <div className="container px-0 d-flex flex-column justify-content-between">
        <div className="column col-md-10">
          <p className="text-gray-1 mb-3">
            Thank you for booking an appointment with us! If you have any
            questions, feel free to reach out. Here’s to your better health.
          </p>
        </div>
      </div>
      <div className="d-flex mt-4 h-100">
        <Form
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          className="d-flex"
        >
          <div className="container px-0 d-flex flex-column justify-content-between">
            <div className="column col-md-12 col-lg-12 col-xl-9 mb-12 mb-md-0">
              <div className="row mb-4 mb-md-5">
                <div className="col-md-12 mb-3 mb-lg-0">
                  <div className="d-flex flex-row align-items-center justify-content-start px-4 py-2 appointment-bg border-0 rounded">
                    <div className="d-flex flex-column w-100">
                      <div className="d-flex flex-row align-items-center gap-3 pt-2 pb-0">
                        <img
                          src="/images/eligibility/note.svg"
                          alt="appointment date and time"
                          className="pathway-icon"
                        />
                        <div>
                          <p className="text-primary-600 mb-1">
                            Appointment date & time
                          </p>
                          <p className="fs-6 fw-bold flow-gap mb-0" key={refreshKey}>
                            {localStorage.getItem("appointment_date")} |{" "}
                            {localStorage.getItem("appointment_time")}
                          </p>
                        </div>
                      </div>
                      <hr className="google-btn w-100" />
                      <div className="d-flex mt-1 align-items-center justify-content-between">
                        <p className="mb-0 text-medium-gray contact">Order ID</p>
                        {localStorage.getItem("orderId") && (
                          <p className="mb-0 text-black text-right">{localStorage.getItem("orderId") || "-"}</p>
                        )}
                        {!localStorage.getItem("orderId") && (
                          <div
                            className="spinner-border spinner-border-sm text-primary"
                            role="status">
                              <span></span>
                          </div>
                        )}
                      </div>
                      <hr className="google-btn my-2" />
                      <div className="d-flex mt-1 gap-1 align-items-center justify-content-between">
                        <p className="mb-0 text-medium-gray contact">Selected pricing plan</p>
                        <p className="mb-0 text-black  text-right">
                          {paymentMode && paymentMode === "SUBSCRIPTION"
                            ? "Treat it clinic subscription"
                            : "Treat it • Initial consultation fee"
                          }
                        </p>
                      </div>
                      <hr className="google-btn my-2" />
                      <div className="d-flex mt-1 gap-1 align-items-center justify-content-between">
                        <p className="mb-0 text-medium-gray contact">Full name</p>
                        <p className="mb-0 text-black text-right">{profileData?.displayName || "-"}</p>
                      </div>
                      <hr className="google-btn my-2" />
                      <div className="d-flex mt-1 gap-1 align-items-center justify-content-between">
                        <p className="mb-0 text-medium-gray contact">Email address</p>
                        <p className="mb-0 text-black">{profileData?.email || "-"}</p>
                      </div>
                      <hr className="google-btn my-2" />
                      <div className="d-flex mt-1 align-items-center justify-content-between">
                        <p className="mb-0 text-medium-gray contact">Phone number</p>
                        <p className="mb-0 text-black">{profileData?.phone || "-"}</p>
                      </div>
                      <hr className="google-btn my-2" />
                      <div className="d-flex mt-1 mb-2 gap-12 align-items-center justify-content-between">
                        <p className="mb-0 text-medium-gray contact">Address</p>
                        <p className="mb-0 text-black text-right">{fullAddress || "-"}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mb-4 mb-md-5">
                <div className="col-md-12 col-lg-12 mb-3 mb-lg-0">
                  <label className="mb-2">
                    How did you hear about us?
                    <span className="text-red-1">*</span>
                  </label>
                  <Select
                    name="referredBy"
                    placeholder="Select"
                    className="form-select shadow-none py-2 bg-transparent fs-6 border card-border rounded pe-2 w-100 custom-placeholder"
                    errorClass="error-text mt-1"
                    options={[
                      { value: "facebook", label: "Facebook" },
                      { value: "instagram", label: "Instagram" },
                      { value: "tiktok", label: "TikTok" },
                      { value: "search", label: "Search" },
                      { value: "google_ads", label: "Google Ads/Online Ads" },
                      { value: "email", label: "Email" },
                      { value: "referral", label: "Referral" },
                      { value: "website", label: "Website" },
                      { value: "instore_ad", label: "InStore Ad" },
                      { value: "flyer", label: "Flyer" },
                      { value: "online_forum", label: "Online Forum" },
                      { value: "seminar_webinar", label: "Seminar-Webinar" },
                      { value: "sales_call", label: "Sales Call" },
                      { value: "other", label: "Other" },
                    ]}
                    onChange={handleChange}
                  />
                </div>
                {referredByState === "other" && (
                  <div className="col-md-12 col-lg-12 mt-3 mb-lg-0">
                    <label className="mb-1">
                      Other<span className="text-red-1">*</span>
                    </label>
                    <TextField
                      name="other"
                      placeholder="Type your answer here..."
                      autoComplete="no"
                      lineType
                      className="p-2 px-3 fs-6 bg-transparent border card-border rounded custom-placeholder"
                    />
                  </div>
                )}
              </div>
              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="stepper-div rounded-1 px-4 py-2">
                    <p className="mt-1 fw-bold mb-5">
                      What should you do next?
                    </p>
                    <div class="col-md-12">
                      <Stepper
                        imgSrc="/images/eligibility/completed.svg"
                        imgAlt="Eligibility"
                        title="Your appointment has been successfully booked"
                        description="Your initial consultation with our clinician is now scheduled."
                        lineType="solid"
                        navDesc={false}
                      />
                      <Stepper
                        imgSrc="/images/eligibility/directbox-inactive.svg"
                        imgAlt="Eligibility"
                        title="Share my GP details"
                        description="Your GP Details will be used to get your health records and assess your suitability for medical cannabis treatment."
                        lineType="dashed"
                        primaryText={false}
                        navDesc={false}
                      />
                      <Stepper
                        imgSrc="/images/eligibility/document-inactive.svg"
                        imgAlt="Document"
                        title="Upload medical documents & add notes"
                        description="Please share supporting documents with us as this often accelerates the approval process."
                        lineType="dashed"
                        primaryText={false}
                        navDesc={false}
                      />
                      <Stepper
                        imgSrc="/images/eligibility/user-tag.svg"
                        imgAlt="Photograph"
                        title="Upload a photograph for your medical cannabis card"
                        description="Upload a photograph of yourself as per the UK digital photo guidelines."
                        lineType="dashed"
                        primaryText={false}
                        navDesc={false}
                      />
                      <Stepper
                        imgSrc="/images/eligibility/note-inactive.svg"
                        imgAlt="Book Appointment"
                        title="Upload KYC identification document"
                        description="Please upload an accepted form of Identification"
                        lineType="dashed"
                        primaryText={false}
                        navDesc={false}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="column d-flex flex-row justify-content-center justify-content-md-end align-items-center">
              <div className="d-flex flex-column-reverse flex-md-row footer-div px-5 px-md-0 py-4 py-md-0">
                <button
                  className={`bg-primary-600 ${(disable) ? "opacity-50" : "opacity-100"} text-white py-2 px-5 w-100 w-md-fit h-fit fw-bold fs-6 border-0 rounded text-nowrap`}
                  type="submit"
                  disabled={!localStorage.getItem("orderId")}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default OrderSuccess;
